import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import './Pricing.css';

const REVENUECAT_IOS_KEY = 'appl_kOrXQIGRYppdhUCpTILawuDbVok';
const REVENUECAT_ANDROID_KEY = 'goog_JtSdqjKNWqPPyJbjMmlAYpzxVDb';

function Pricing() {
  const [interval, setInterval] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [circleLoading, setCircleLoading] = useState(null); // which circle tier is loading
  const [portalLoading, setPortalLoading] = useState(false);
  const [rcOffering, setRcOffering] = useState(null);
  const [rcCircleOffering, setRcCircleOffering] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  useEffect(() => {
    fetchSubscriptionStatus();
    if (isNative) {
      initRevenueCat();
    }
  }, []);

  // ── RevenueCat Init ──
  const initRevenueCat = async () => {
    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      const apiKey = platform === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
      await Purchases.configure({ apiKey });

      const offerings = await Purchases.getOfferings();

      // Current offering = Pro
      if (offerings.current) {
        setRcOffering(offerings.current);
      }

      // All 3 circle packages are in one offering: circle_plans
      const circlePlans = offerings.all?.['circle_plans'];
      if (circlePlans) {
        setRcCircleOffering(circlePlans);
      }
    } catch (e) {
      console.error('RevenueCat init error:', e);
    }
  };

  // ── Subscription Status ──
  const fetchSubscriptionStatus = async () => {
    try {
      if (isNative) {
        const apiKey = platform === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
        await Purchases.configure({ apiKey });
        const { customerInfo } = await Purchases.getCustomerInfo();

        const proEnt = customerInfo.entitlements.active['pro'];

        if (proEnt) {
          // Determine actual plan from product identifier
          const productId = (proEnt.productIdentifier || '').toLowerCase();
          let plan = 'pro';
          if (productId.includes('circle_10'))     plan = 'circle_10';
          else if (productId.includes('circle_5')) plan = 'circle_5';
          else if (productId.includes('circle_3')) plan = 'circle_3';

          setCurrentPlan({
            plan,
            status: 'active',
            cancelAtPeriodEnd: proEnt.willRenew === false,
            currentPeriodEnd: proEnt.expirationDate,
          });
          await syncPlanToBackend(plan);
        } else {
          // RevenueCat shows nothing — fall back to backend which may have synced plan
          console.log('RC no active entitlements — falling back to backend');
          const response = await axios.get(`${API_URL}/api/billing/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCurrentPlan(response.data);
        }
      } else {
        const response = await axios.get(`${API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentPlan(response.data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setCurrentPlan({ plan: 'free', status: 'inactive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Upgrade to Pro ──
  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      if (isNative) {
        if (!rcOffering) { alert('Unable to load products. Please try again.'); return; }
        const pkg = rcOffering.monthly;
        if (!pkg) { alert('Monthly plan not available. Please try again.'); return; }
        const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
        const entitlement = customerInfo.entitlements.active['pro'];
        if (entitlement) {
          setCurrentPlan({ plan: 'pro', status: 'active', cancelAtPeriodEnd: false });
          await syncPlanToBackend('pro');
          alert('Welcome to AirFlyp Pro! 🎉');
        }
      } else {
        const response = await axios.post(
          `${API_URL}/api/billing/create-checkout`,
          { interval },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = response.data.url;
      }
    } catch (error) {
      if (error?.code !== 'PURCHASE_CANCELLED') {
        console.error('Error during upgrade:', error);
        alert('Purchase failed. Please try again.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  // ── Purchase Circle Plan ──
  const handleCirclePurchase = async (tier) => {
    // tier = 'circle_3' | 'circle_5' | 'circle_10'
    setCircleLoading(tier);
    try {
      if (isNative) {
        if (!rcCircleOffering) {
          alert('Circle plans unavailable. Please try again.');
          return;
        }

        // Find the right package by matching product identifier
        const packages = rcCircleOffering.availablePackages || [];
        const pkg = packages.find(p =>
          p.identifier === tier ||
          p.identifier?.includes(tier) ||
          p.product?.identifier?.includes(tier)
        );

        if (!pkg) {
          alert(`${tier} plan not available. Please try again.`);
          return;
        }

        const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
        const circleEnt = customerInfo.entitlements.active['circle'];
        const proEnt    = customerInfo.entitlements.active['pro'];

        if (circleEnt || proEnt) {
          setCurrentPlan({ plan: tier, status: 'active', cancelAtPeriodEnd: false });
          await syncPlanToBackend(tier);
          alert(`Welcome to AirFlyp Circle! 🎉 Your team workspace is ready.`);
          navigate('/dashboard');
        }
      } else {
        // Web — Stripe checkout for Circle
        const response = await axios.post(
          `${API_URL}/api/billing/circle-checkout`,
          { tier },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = response.data.url;
      }
    } catch (error) {
      if (error?.code !== 'PURCHASE_CANCELLED') {
        console.error('Circle purchase error:', error);
        alert('Purchase failed. Please try again.');
      }
    } finally {
      setCircleLoading(null);
    }
  };

  // ── Sync plan to backend ──
  const syncPlanToBackend = async (plan) => {
    try {
      await axios.post(
        `${API_URL}/api/billing/sync-mobile`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to sync plan to backend:', error);
    }
  };

  // ── Restore Purchases ──
  const handleRestorePurchases = async () => {
    setRestoreLoading(true);
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const proEnt = customerInfo.entitlements.active['pro'];

      if (proEnt) {
        const productId = (proEnt.productIdentifier || '').toLowerCase();
        let plan = 'pro';
        if (productId.includes('circle_10'))     plan = 'circle_10';
        else if (productId.includes('circle_5')) plan = 'circle_5';
        else if (productId.includes('circle_3')) plan = 'circle_3';

        setCurrentPlan({ plan, status: 'active', cancelAtPeriodEnd: false });
        await syncPlanToBackend(plan);
        const label = plan.startsWith('circle') ? 'Circle' : 'Pro';
        alert(`Purchases restored! Welcome back to AirFlyp ${label} 🎉`);
      } else {
        alert('No previous purchases found for this account.');
      }
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore purchases. Please try again.');
    } finally {
      setRestoreLoading(false);
    }
  };

  // ── Manage Subscription ──
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      if (isNative) {
        if (platform === 'ios') {
          window.open('https://apps.apple.com/account/subscriptions', '_system');
        } else {
          window.open('https://play.google.com/store/account/subscriptions', '_system');
        }
      } else {
        const response = await axios.post(
          `${API_URL}/api/billing/portal`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error opening subscription management:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const isPro    = ['pro'].includes(currentPlan?.plan?.toLowerCase()) && currentPlan?.status === 'active';
  const isCircle = ['circle_member','circle_3','circle_5','circle_10'].includes(currentPlan?.plan?.toLowerCase()) && currentPlan?.status === 'active';
  const isPaid   = isPro || isCircle;

  if (loading) {
    return (
      <div className="pricing-page">
        <div className="pricing-loading">
          <div className="pricing-spinner"></div>
          <p>Loading your plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <button className="pricing-back-btn" onClick={() => navigate('/dashboard')}>
        ← Back to Dashboard
      </button>

      {/* Header */}
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Start free, upgrade when you're ready to grow your practice</p>

        {!isNative && (
          <div className="interval-toggle">
            <button className={`interval-btn ${interval === 'monthly' ? 'active' : ''}`} onClick={() => setInterval('monthly')}>Monthly</button>
            <button className={`interval-btn ${interval === 'annual' ? 'active' : ''}`} onClick={() => setInterval('annual')}>
              Annual <span className="save-badge">Save 20%</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Free + Pro ── */}
      <div className="pricing-cards">

        {/* Free */}
        <div className={`pricing-card ${!isPaid ? 'current' : ''}`}>
          {!isPaid && <div className="current-plan-badge">Current Plan</div>}
          <div className="card-header">
            <h2>Free</h2>
            <div className="card-price">
              <span className="price-amount">$0</span>
              <span className="price-period">forever</span>
            </div>
          </div>
          <p className="card-description">Perfect for getting started</p>
          <ul className="feature-list">
            <li className="included"><span className="check">✓</span> Up to 3 clients</li>
            <li className="included"><span className="check">✓</span> Basic transcription</li>
            <li className="included"><span className="check">✓</span> Appointment scheduling</li>
            <li className="included"><span className="check">✓</span> 1 client survey</li>
            <li className="included"><span className="check">✓</span> Session notes</li>
            <li className="excluded"><span className="x">✗</span> AI Persona</li>
            <li className="excluded"><span className="x">✗</span> 1:1 Video sessions</li>
            <li className="excluded"><span className="x">✗</span> Billing & revenue tracking</li>
            <li className="excluded"><span className="x">✗</span> Priority support</li>
          </ul>
          {!isPaid && <button className="plan-btn free-btn" disabled>Your Current Plan</button>}
        </div>

        {/* Pro */}
        <div className={`pricing-card pro ${isPro ? 'current' : ''}`}>
          {isPro && <div className="current-plan-badge pro-badge">Current Plan</div>}
          {!isPaid && <div className="popular-badge">Most Popular</div>}
          <div className="card-header">
            <h2>Pro</h2>
            <div className="card-price">
              <span className="price-amount">${isNative ? '29' : interval === 'monthly' ? '29' : '23'}</span>
              <span className="price-period">/mo{!isNative && interval === 'annual' && <span className="billed-note">billed annually at $278</span>}</span>
            </div>
          </div>
          <p className="card-description">Everything you need to scale your practice</p>
          <ul className="feature-list">
            <li className="included"><span className="check">✓</span> Unlimited clients</li>
            <li className="included"><span className="check">✓</span> Full transcription with history</li>
            <li className="included"><span className="check">✓</span> Unlimited surveys</li>
            <li className="included highlight"><span className="check">✓</span> AI Persona chatbot</li>
            <li className="included highlight"><span className="check">✓</span> 1:1 Video sessions</li>
            <li className="included highlight"><span className="check">✓</span> Billing & revenue tracking</li>
            <li className="included highlight"><span className="check">✓</span> AI-powered insights</li>
            <li className="included highlight"><span className="check">✓</span> Priority support</li>
          </ul>
          {isPro ? (
            <button className="plan-btn manage-btn" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          ) : (
            <button className="plan-btn upgrade-btn" onClick={handleUpgrade} disabled={checkoutLoading || isCircle}>
              {checkoutLoading ? 'Processing...' : `Upgrade to Pro — $${isNative || interval === 'monthly' ? '29/mo' : '278/yr'}`}
            </button>
          )}
        </div>
      </div>

      {/* ── Circle Plans ── */}
      <div className="pricing-section-label">
        <span>✦✦</span> Circle — Team Workspaces
      </div>
      <p className="pricing-section-desc">Share clients, notes, calendars and AI tools across your whole team. Every Circle member gets full Pro features.</p>

      <div className="pricing-cards circle-cards">

        {/* Circle 3 */}
        <div className={`pricing-card circle ${currentPlan?.plan === 'circle_3' ? 'current' : ''}`}>
          {currentPlan?.plan === 'circle_3' && <div className="current-plan-badge">Current Plan</div>}
          <div className="card-header">
            <h2>Circle 3</h2>
            <div className="card-price">
              <span className="price-amount">$79</span>
              <span className="price-period">/mo</span>
            </div>
          </div>
          <p className="card-description">Up to 3 team members</p>
          <ul className="feature-list">
            <li className="included"><span className="check">✓</span> 3 members — all get Pro</li>
            <li className="included"><span className="check">✓</span> Shared client workspace</li>
            <li className="included"><span className="check">✓</span> Team calendar</li>
            <li className="included"><span className="check">✓</span> Shared AI personas</li>
            <li className="included"><span className="check">✓</span> Circle notifications</li>
          </ul>
          {currentPlan?.plan === 'circle_3' ? (
            <button className="plan-btn manage-btn" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              className="plan-btn circle-btn"
              onClick={() => handleCirclePurchase('circle_3')}
              disabled={circleLoading === 'circle_3' || isCircle}
            >
              {circleLoading === 'circle_3' ? 'Processing...' : 'Get Circle 3 — $79/mo'}
            </button>
          )}
        </div>

        {/* Circle 5 */}
        <div className={`pricing-card circle featured-circle ${currentPlan?.plan === 'circle_5' ? 'current' : ''}`}>
          {currentPlan?.plan === 'circle_5' && <div className="current-plan-badge">Current Plan</div>}
          <div className="popular-badge">Most Popular</div>
          <div className="card-header">
            <h2>Circle 5</h2>
            <div className="card-price">
              <span className="price-amount">$129</span>
              <span className="price-period">/mo</span>
            </div>
          </div>
          <p className="card-description">Up to 5 team members</p>
          <ul className="feature-list">
            <li className="included"><span className="check">✓</span> 5 members — all get Pro</li>
            <li className="included"><span className="check">✓</span> Shared client workspace</li>
            <li className="included"><span className="check">✓</span> Team calendar</li>
            <li className="included"><span className="check">✓</span> Shared AI personas</li>
            <li className="included"><span className="check">✓</span> Circle notifications</li>
          </ul>
          {currentPlan?.plan === 'circle_5' ? (
            <button className="plan-btn manage-btn" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              className="plan-btn circle-btn"
              onClick={() => handleCirclePurchase('circle_5')}
              disabled={circleLoading === 'circle_5' || isCircle}
            >
              {circleLoading === 'circle_5' ? 'Processing...' : 'Get Circle 5 — $129/mo'}
            </button>
          )}
        </div>

        {/* Circle 10 */}
        <div className={`pricing-card circle ${currentPlan?.plan === 'circle_10' ? 'current' : ''}`}>
          {currentPlan?.plan === 'circle_10' && <div className="current-plan-badge">Current Plan</div>}
          <div className="card-header">
            <h2>Circle 10</h2>
            <div className="card-price">
              <span className="price-amount">$199</span>
              <span className="price-period">/mo</span>
            </div>
          </div>
          <p className="card-description">Up to 10 team members</p>
          <ul className="feature-list">
            <li className="included"><span className="check">✓</span> 10 members — all get Pro</li>
            <li className="included"><span className="check">✓</span> Shared client workspace</li>
            <li className="included"><span className="check">✓</span> Team calendar</li>
            <li className="included"><span className="check">✓</span> Shared AI personas</li>
            <li className="included"><span className="check">✓</span> Circle notifications</li>
          </ul>
          {currentPlan?.plan === 'circle_10' ? (
            <button className="plan-btn manage-btn" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              className="plan-btn circle-btn"
              onClick={() => handleCirclePurchase('circle_10')}
              disabled={circleLoading === 'circle_10' || isCircle}
            >
              {circleLoading === 'circle_10' ? 'Processing...' : 'Get Circle 10 — $199/mo'}
            </button>
          )}
        </div>
      </div>

      {/* Cancellation notice */}
      {isPaid && currentPlan?.cancelAtPeriodEnd && (
        <div className="cancellation-notice">
          <p>Your plan is set to cancel on <strong>{new Date(currentPlan.currentPeriodEnd).toLocaleDateString()}</strong>. You'll keep access until then.</p>
          <button className="plan-btn manage-btn" onClick={handleManageSubscription}>Reactivate Subscription</button>
        </div>
      )}

      {/* Mobile billing note */}
      {isNative && (
        <p className="mobile-billing-note">
          Subscriptions are managed through {platform === 'ios' ? 'the App Store' : 'Google Play'}.
        </p>
      )}

      {/* Legal — required by Apple */}
      {isNative && !isPaid && (
        <div className="subscription-legal">
          <button className="restore-btn" onClick={handleRestorePurchases} disabled={restoreLoading}>
            {restoreLoading ? 'Restoring...' : 'Restore Purchases'}
          </button>
          <div className="legal-links">
            <a href="https://www.airflyp.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <span> · </span>
            <a href="https://www.airflyp.com/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          </div>
          <p className="subscription-terms-note">
            Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period. Manage or cancel in your {platform === 'ios' ? 'App Store' : 'Google Play'} account settings.
          </p>
          <p className="ai-disclosure-note">
            By using AI features, you consent to your session data being processed by OpenAI and ElevenLabs to provide transcription, insights, and voice services. See our <a href="https://www.airflyp.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.
          </p>
        </div>
      )}

      {/* FAQ */}
      <div className="pricing-faq">
        <h3>Questions?</h3>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I switch between monthly and annual?</h4>
            <p>Yes! Change your billing cycle anytime from the Manage Subscription portal.</p>
          </div>
          <div className="faq-item">
            <h4>What happens to my data if I downgrade?</h4>
            <p>Your data is always safe. On the Free plan you'll still have access with the 3-client limit.</p>
          </div>
          <div className="faq-item">
            <h4>Can I cancel anytime?</h4>
            <p>Absolutely. Cancel anytime and keep access through the end of your billing period.</p>
          </div>
          <div className="faq-item">
            <h4>How do Circle members get access?</h4>
            <p>The Circle owner invites members via email. Each member gets full Pro features automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;